import type { CSSProperties, ReactNode } from 'react'
import { getComponentMeta, getElementMeta, type MapElementKind } from './catalog'

interface MapPinProps {
  /** Color del cuerpo del pin. */
  color: string
  /** Icono centrado en la cabeza del pin (se espera blanco, ~14px). */
  icon?: ReactNode
  /** Alto total del pin en px (default 40; el ancho escala proporcional). */
  size?: number
  /** Badge opcional en la esquina superior derecha (p.ej. número de orden). */
  badge?: ReactNode
}

// Geometría del pin clásico (gota) en viewBox 32×42: cabeza circular centrada
// en (16,15.5) y punta en (16,41). El AdvancedMarker ancla su contenido en
// bottom-center por defecto, que coincide con la punta del pin.
const PIN_PATH = 'M16 1C7.85 1 1.5 7.44 1.5 15.5c0 3.62 1.51 6.94 3.85 9.85L16 41l10.65-15.65c2.34-2.91 3.85-6.23 3.85-9.85C30.5 7.44 24.15 1 16 1z'

/**
 * Marker estándar del sistema: pin clásico de mapa con un icono al centro y
 * color único por tipo de elemento. Usarlo como hijo de `AdvancedMarker` SIN
 * `anchorPoint` (el ancla por defecto bottom-center coincide con la punta).
 * Preferir los helpers `ComponentPin` / `ElementPin`, que resuelven icono y
 * color desde el catálogo (`catalog.tsx`).
 */
export function MapPin({ color, icon, size = 40, badge }: MapPinProps) {
  const width = (size * 32) / 42

  const iconStyle: CSSProperties = {
    position: 'absolute',
    left: '50%',
    // Centro de la cabeza del pin (15.5/42 del alto total).
    top: `${(15.5 / 42) * 100}%`,
    transform: 'translate(-50%, -50%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    pointerEvents: 'none',
  }

  return (
    <div style={{ position: 'relative', width, height: size }}>
      <svg
        width={width}
        height={size}
        viewBox="0 0 32 42"
        style={{ display: 'block', filter: 'drop-shadow(0 2px 3px rgba(0,0,0,0.35))' }}
      >
        <path d={PIN_PATH} fill={color} stroke="white" strokeWidth="1.5" />
      </svg>
      {icon && <div style={iconStyle}>{icon}</div>}
      {badge && (
        <div
          style={{
            position: 'absolute',
            top: -4,
            right: -6,
            minWidth: 16,
            height: 16,
            padding: '0 3px',
            borderRadius: 8,
            background: 'white',
            color: '#1E293B',
            fontSize: 10,
            fontWeight: 700,
            lineHeight: '16px',
            textAlign: 'center',
            boxShadow: '0 1px 2px rgba(0,0,0,0.3)',
            fontFamily: "'Instrument Sans', system-ui, sans-serif",
          }}
        >
          {badge}
        </div>
      )}
    </div>
  )
}

interface ComponentPinProps {
  /** Código estable del tipo de componente (ComponentType.Code). */
  code: string | null | undefined
  size?: number
  badge?: ReactNode
  /** Override de color (p.ej. rojo si el componente está fuera del polígono). */
  color?: string
}

/** Pin de componente minero: icono y color según el catálogo por código de tipo. */
export function ComponentPin({ code, size, badge, color }: ComponentPinProps) {
  const meta = getComponentMeta(code)
  return <MapPin color={color ?? meta.color} icon={meta.icon} size={size} badge={badge} />
}

interface ElementPinProps {
  /** Tipo de elemento de mapa (camión, origen, parada, foto…). */
  kind: MapElementKind
  size?: number
  badge?: ReactNode
  /** Override de color para tipos con color dinámico (camión por traslado, evento por estado). */
  color?: string
}

/** Pin de elemento de mapa: icono fijo por tipo, color del catálogo u override. */
export function ElementPin({ kind, size, badge, color }: ElementPinProps) {
  const meta = getElementMeta(kind)
  return <MapPin color={color ?? meta.color} icon={meta.icon} size={size} badge={badge} />
}
